import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { MenuBookOutlined } from '@mui/icons-material';
import { RecipeV2Detail } from '../types';
import { getMenuUsageForRecipe } from '../../../mocks/data/recipeUsageData';

interface UsageTabProps {
  recipe: RecipeV2Detail;
}

const statusColor: Record<string, 'success' | 'warning' | 'default'> = {
  Live: 'success',
  Scheduled: 'warning',
};

/** Demo Mode only - shows which Menus reference this recipe. Feeds Item Rationalisation's
 * "used in any menu" signal and the wider Menus > Recipes traceability chain. */
const UsageTab = ({ recipe }: UsageTabProps) => {
  const { t } = useTranslation('recipeV2');
  const navigate = useNavigate();
  const usage = getMenuUsageForRecipe(recipe.id);

  if (usage.length === 0) {
    return (
      <Paper variant="outlined"
        sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          {t('usage.notUsed', 'This recipe is not currently used in any planned or live menu.')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={3}>
      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Stack direction="row"
          alignItems="center"
          spacing={1}>
          <MenuBookOutlined color="action" />
          <Typography variant="textXL">{t('usage.title', 'Used in')}</Typography>
        </Stack>
      </Paper>
      <TableContainer component={Paper}
        variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('usage.menu', 'Menu')}</TableCell>
              <TableCell>{t('usage.sites', 'Site(s)')}</TableCell>
              <TableCell>{t('usage.servicePeriod', 'Service period')}</TableCell>
              <TableCell>{t('statusLabel')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usage.map((entry) => (
              <TableRow
                key={entry.menuId}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/menus/${entry.menuId}`)}
              >
                <TableCell>
                  <Typography variant="textSM"
                    fontWeight={600}
                    color="primary.main">{entry.menuName}</Typography>
                </TableCell>
                <TableCell>{entry.siteName}</TableCell>
                <TableCell>{entry.servicePeriod}</TableCell>
                <TableCell>
                  <Chip label={entry.status}
                    size="small"
                    color={statusColor[entry.status] ?? 'default'} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default UsageTab;
